import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, formatRON, devWarn } from "../lib/api";
import { invoiceToFormState } from "../lib/invoiceForm";
import {
  ColorPicker,
  collectAllColors,
  colorPurchasePrice,
  applicableColors,
  selKey,
  colorSlug,
  MAT_COLOR,
} from "../components/ColorPicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { FileDown, Folder, Search, RotateCcw, Plus, X, UserCog, Save } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { ClientAutocomplete } from "../components/ClientAutocomplete";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const NO_CATEGORY = "__none__";

function buildSheetsAutoNote(sheets) {
  const valid = (Array.isArray(sheets) ? sheets : []).filter(
    (s) => Number(s.length || 0) > 0 && Number(s.count || 0) > 0
  );
  if (!valid.length) return "";
  const parts = valid.map((s) => `${s.length}*${s.count}`).join(" + ");
  return `Lungime*Coli: ${parts}`;
}

function combineNotes(autoNote, userNote) {
  const a = (autoNote || "").trim();
  const u = (userNote || "").trim();
  if (a && u) return `${a} | ${u}`;
  return a || u;
}

// Compute actual quantity for a material given its current selection state.
function computeQty(material, sel) {
  if (!sel) return 0;
  const hasWidth = Number(material.width_m || 0) > 0;
  if (!hasWidth) return Number(sel.quantity || 0);
  const mode = sel.mode || "sheets";
  if (mode === "direct") return Number(sel.direct_m2 || 0);
  const w = Number(material.width_m);
  const sheets = Array.isArray(sel.sheets) ? sel.sheets : [];
  const sum = sheets.reduce(
    (a, s) => a + Number(s.length || 0) * w * Number(s.count || 0),
    0
  );
  return Number(sum.toFixed(3));
}

export default function DevizPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientLastName, setClientLastName] = useState("");
  const [clientFirstName, setClientFirstName] = useState("");
  const [clientCui, setClientCui] = useState("");
  const [clientCnp, setClientCnp] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientRegCom, setClientRegCom] = useState("");
  const [delegateName, setDelegateName] = useState("");
  const [agentName, setAgentName] = useState("");
  const [savingAgent, setSavingAgent] = useState(false);
  const [selections, setSelections] = useState({});
  const [selectedColors, setSelectedColors] = useState([MAT_COLOR]);
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [customNumber, setCustomNumber] = useState("");
  const [suggestedNumber, setSuggestedNumber] = useState(null);
  const [includePrices, setIncludePrices] = useState(false);
  const [includeCodes, setIncludeCodes] = useState(true);
  const [sourceId, setSourceId] = useState(null);

  const load = async ({ silent = false } = {}) => {
    try {
      const [m, c] = await Promise.all([api.get("/materials"), api.get("/categories")]);
      setMaterials(m.data);
      setCategories(c.data);
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Suggested next auto number for the placeholder.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/next-number/deviz");
        if (!cancelled) setSuggestedNumber(data?.number ?? null);
      } catch (err) { devWarn("next-number fetch failed:", err); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (user?.agent_name) setAgentName(user.agent_name);
  }, [user?.agent_name]);

  // Auto-fill when editing an existing deviz: ?from=<id>.
  useEffect(() => {
    const fromId = searchParams.get("from");
    if (!fromId || materials.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: inv } = await api.get(`/invoices/${fromId}`);
        if (cancelled) return;
        const s = invoiceToFormState(inv, materials);
        setSelections(s.selections);
        setClientLastName(s.client.lastName);
        setClientFirstName(s.client.firstName);
        setClientCui(s.client.cui);
        setClientCnp(s.client.cnp);
        setClientAddress(s.client.address);
        setClientPhone(s.client.phone);
        setClientRegCom(s.client.regCom);
        setDelegateName(s.delegateName);
        setIncludePrices(!!s.includePrices);
        setIncludeCodes(s.includeCodes);
        setSelectedColors(s.selectedColors || [MAT_COLOR]);
        setSourceId(inv.type === "factura" ? inv.id : null);
        toast.success(
          inv.type === "deviz"
            ? "Deviz incarcat. Modifica si descarca o noua versiune."
            : "Factura preluata in deviz. Verifica si descarca devizul."
        );
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
      } finally {
        if (!cancelled) setSearchParams({}, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, materials, setSearchParams]);

  const saveAgentName = async () => {
    setSavingAgent(true);
    try {
      await api.put("/auth/me/agent-name", { agent_name: agentName });
      toast.success("Nume agent salvat");
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setSavingAgent(false);
    }
  };

  const pickClient = (c) => {
    setClientLastName(c.last_name || "");
    setClientFirstName(c.first_name || "");
    setClientCui(c.cui || "");
    setClientCnp(c.cnp || "");
    setClientAddress(c.address || "");
    setClientPhone(c.phone || "");
    setClientRegCom(c.reg_com || "");
  };

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, []);

  const updateSel = (id, patch) => {
    setSelections((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const resetSelections = () => {
    if (!window.confirm("Sterge toate cantitatile introduse?")) return;
    setSelections({});
    setSourceId(null);
  };

  const categoriesById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c;
    return m;
  }, [categories]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filter = (m) =>
      !q ||
      m.name.toLowerCase().includes(q) ||
      (m.code || "").toLowerCase().includes(q);
    const order = categories.map((c) => c.id);
    const buckets = {};
    for (const id of order) buckets[id] = [];
    buckets[NO_CATEGORY] = [];
    for (const m of materials) {
      if (!filter(m)) continue;
      const k = m.category_id && buckets[m.category_id] ? m.category_id : NO_CATEGORY;
      buckets[k].push(m);
    }
    const result = order
      .filter((id) => buckets[id].length > 0)
      .map((id) => ({ id, name: categoriesById[id]?.name || "—", items: buckets[id] }));
    if (buckets[NO_CATEGORY].length > 0) {
      result.push({ id: NO_CATEGORY, name: "Fara categorie", items: buckets[NO_CATEGORY] });
    }
    return result;
  }, [materials, categories, categoriesById, search]);

  const allColors = useMemo(() => collectAllColors(materials), [materials]);
  const appColors = (m) => applicableColors(m, selectedColors);
  const itemColor = (m, color) => ((m.colors || []).length > 0 ? color : "");

  const previewRows = useMemo(() => {
    const rows = [];
    for (const m of materials) {
      for (const color of applicableColors(m, selectedColors)) {
        const sel = selections[selKey(m.id, color)];
        const qty = computeQty(m, sel);
        if (qty <= 0) continue;
        const row = {
          material_id: m.id,
          color: itemColor(m, color),
          name: m.name,
          unit: m.unit || "",
          quantity: qty,
          notes: (sel?.notes || "").trim(),
        };
        if (includePrices) {
          const unitPrice = colorPurchasePrice(m, color) * (1 + Number(m.markup) / 100);
          row.unit_price = unitPrice;
          row.line_total = unitPrice * qty;
        }
        rows.push(row);
      }
    }
    return rows;
  }, [materials, selections, selectedColors, includePrices]);

  const previewTotal = useMemo(() => {
    if (!includePrices) return 0;
    return previewRows.reduce((acc, r) => acc + Number(r.line_total || 0), 0);
  }, [previewRows, includePrices]);

  const downloadDocx = async () => {
    if (previewRows.length === 0) {
      toast.error("Introdu cantitatea pentru cel putin un material");
      return;
    }
    setDownloading(true);
    try {
      const items = [];
      for (const m of materials) {
        for (const color of applicableColors(m, selectedColors)) {
          const sel = selections[selKey(m.id, color)];
          const qty = computeQty(m, sel);
          if (qty > 0) {
            const hasWidth = Number(m.width_m || 0) > 0;
            const inSheetsMode = hasWidth && (sel?.mode || "sheets") !== "direct";
            const autoNote = inSheetsMode ? buildSheetsAutoNote(sel?.sheets) : "";
            const finalNotes = combineNotes(autoNote, sel?.notes);
            items.push({
              material_id: m.id,
              quantity: qty,
              notes: finalNotes,
              color: itemColor(m, color),
            });
          }
        }
      }
      const res = await api.post(
        "/deviz/docx",
        (() => {
          const body = {
            client_last_name: clientLastName,
            client_first_name: clientFirstName,
            client_cui: clientCui,
            client_cnp: clientCnp,
            client_address: clientAddress,
            client_phone: clientPhone,
            client_reg_com: clientRegCom,
            delegate_name: delegateName,
            agent_name: agentName,
            include_prices: includePrices,
            include_codes: includeCodes,
            source_id: sourceId,
            items,
          };
          const parsedNum = parseInt(customNumber, 10);
          if (!isNaN(parsedNum) && parsedNum > 0) body.number = parsedNum;
          return body;
        })(),
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = `${clientLastName || ""}-${clientFirstName || ""}`
        .replace(/^-|-$/g, "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Za-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      a.download = `deviz-${slug || Date.now()}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Deviz descarcat");
      setCustomNumber("");
      try {
        const { data } = await api.get("/next-number/deviz");
        setSuggestedNumber(data?.number ?? null);
      } catch (err) { devWarn("next-number refresh failed:", err); }
    } catch (e) {
      toast.error("Eroare la descarcare");
    } finally {
      setDownloading(false);
    }
  };

  // ---- Width-based material: render mode toggle + conditional inputs ----
  const renderWidthInput = (m, sk, tid) => {
    const sel = selections[sk] || {};
    const mode = sel.mode || "sheets";
    const w = Number(m.width_m);
    const sheets =
      Array.isArray(sel.sheets) && sel.sheets.length > 0
        ? sel.sheets
        : [{ length: "", count: "" }];
    const qty = computeQty(m, sel);

    const updateSheet = (i, patch) => {
      const copy = sheets.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
      updateSel(sk, { sheets: copy, mode: "sheets" });
    };
    const addSheet = () => {
      // Stable id so row keys survive add/remove without React state mix-ups.
      const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}-${Math.random()}`;
      updateSel(sk, { sheets: [...sheets, { id, length: "", count: "" }], mode: "sheets" });
    };
    const removeSheet = (i) => {
      const copy = sheets.filter((_, idx) => idx !== i);
      updateSel(sk, { sheets: copy.length ? copy : [{ length: "", count: "" }] });
    };

    return (
      <div className="space-y-1.5">
        {/* Mode toggle */}
        <div className="flex rounded-sm border border-zinc-300 overflow-hidden w-full">
          <button
            type="button"
            onClick={() => updateSel(sk, { mode: "sheets" })}
            className={`flex-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition ${
              mode !== "direct"
                ? "bg-black text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid={`deviz-mat-mode-sheets-${tid}`}
          >
            Coli
          </button>
          <button
            type="button"
            onClick={() => updateSel(sk, { mode: "direct" })}
            className={`flex-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition border-l border-zinc-300 ${
              mode === "direct"
                ? "bg-black text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid={`deviz-mat-mode-direct-${tid}`}
          >
            m² direct
          </button>
        </div>

        {mode === "direct" ? (
          <Input
            type="number"
            min="0"
            step="0.01"
            value={sel.direct_m2 ?? ""}
            onChange={(e) => updateSel(sk, { direct_m2: e.target.value, mode: "direct" })}
            placeholder="m² total"
            className="h-8 rounded-sm border-zinc-300 focus-visible:ring-black text-sm"
            data-testid={`deviz-mat-direct-${tid}`}
          />
        ) : (
          <div className="space-y-1">
            {sheets.map((s, i) => (
              <div key={s.id || `idx-${i}`} className="flex gap-1 items-center">
                <Input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  value={s.length ?? ""}
                  onChange={(e) => updateSheet(i, { length: e.target.value })}
                  placeholder="Lungime"
                  className="h-10 min-w-[6rem] flex-1 px-2 rounded-sm border-zinc-300 focus-visible:ring-black text-base font-mono [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  data-testid={`deviz-mat-sheet-length-${tid}-${i}`}
                />
                <span className="text-zinc-500 text-sm shrink-0">×</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={s.count ?? ""}
                  onChange={(e) => updateSheet(i, { count: e.target.value })}
                  placeholder="Coli"
                  className="h-10 w-16 md:w-20 shrink-0 px-1 rounded-sm border-zinc-300 focus-visible:ring-black text-base font-mono text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  data-testid={`deviz-mat-sheet-count-${tid}-${i}`}
                />
                {sheets.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSheet(i)}
                    className="text-red-500 hover:text-red-700 p-1"
                    data-testid={`deviz-mat-sheet-remove-${tid}-${i}`}
                    title="Sterge"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addSheet}
              className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 hover:text-black flex items-center gap-1 py-0.5"
              data-testid={`deviz-mat-sheet-add-${tid}`}
            >
              <Plus size={10} /> Adauga lungime
            </button>
          </div>
        )}

        <div className="text-[10px] text-zinc-600 font-mono pt-0.5">
          latime {w}m · {qty > 0 ? `${qty} m²` : "—"}
        </div>
      </div>
    );
  };

  const renderMaterialRow = (m, color) => {
    const sk = selKey(m.id, color);
    const tid = color === MAT_COLOR ? m.id : `${m.id}-${colorSlug(color)}`;
    const sel = selections[sk] || {};
    const hasWidth = Number(m.width_m || 0) > 0;
    const qty = computeQty(m, sel);
    const isActive = qty > 0;
    const showColorChip = (m.colors || []).length > 0;
    return (
      <TableRow
        key={sk}
        className={isActive ? "bg-amber-50/40" : ""}
        data-testid={`deviz-mat-row-${tid}`}
      >
        <TableCell className="text-black font-medium align-top py-2">
          {m.name}
          {showColorChip && (
            <span
              className={`ml-2 inline-block px-1.5 py-0 rounded-sm font-mono text-[10px] align-middle border ${
                color === MAT_COLOR
                  ? "bg-zinc-100 border-zinc-200 text-zinc-700"
                  : "bg-black text-white border-black"
              }`}
              data-testid={`deviz-mat-color-chip-${tid}`}
            >
              {color}
            </span>
          )}
        </TableCell>
        <TableCell className="text-center text-zinc-700 font-mono text-xs align-top py-2">
          {m.unit || "—"}
        </TableCell>
        <TableCell className="align-top py-2">
          {hasWidth ? (
            renderWidthInput(m, sk, tid)
          ) : (
            <Input
              type="number"
              min="0"
              step="0.01"
              value={sel.quantity ?? ""}
              onChange={(e) => updateSel(sk, { quantity: e.target.value })}
              placeholder="0"
              className="h-8 rounded-sm border-zinc-300 focus-visible:ring-black text-sm"
              data-testid={`deviz-mat-qty-${tid}`}
            />
          )}
        </TableCell>
        <TableCell className="align-top py-2">
          <Textarea
            value={sel.notes ?? ""}
            onChange={(e) => updateSel(sk, { notes: e.target.value })}
            placeholder="—"
            rows={1}
            className="rounded-sm border-zinc-300 focus-visible:ring-black resize-y min-h-[32px] text-sm py-1"
            data-testid={`deviz-mat-notes-${tid}`}
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="p-6 md:p-10" data-testid="deviz-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
            Estimare materiale
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-black">
            Deviz materiale
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Introdu cantitatile direct in lista. Doar materialele cu cantitate &gt; 0 apar pe deviz.
            Activeaza optiunea de mai jos daca vrei sa apara si preturile + TOTAL DE PLATA pe deviz.
          </p>
          {/* Include-prices toggle — affects DOCX columns + preview */}
          <label
            className="mt-4 inline-flex items-center gap-2.5 cursor-pointer select-none px-3 py-2 rounded-sm border border-zinc-300 bg-white hover:border-zinc-500 transition"
            data-testid="deviz-include-prices-toggle"
          >
            <input
              type="checkbox"
              checked={includePrices}
              onChange={(e) => setIncludePrices(e.target.checked)}
              className="h-4 w-4 accent-black"
              data-testid="deviz-include-prices-checkbox"
            />
            <span className="font-mono uppercase text-[11px] tracking-wider text-black font-semibold">
              Include preturi pe deviz
            </span>
            <span className="font-mono text-[10px] text-zinc-500">
              (Pret unitar · Valoarea · TOTAL DE PLATA)
            </span>
          </label>
          {/* Include-codes toggle — controls the "Cod" column on the DOCX */}
          <label
            className="mt-4 ml-2 inline-flex items-center gap-2.5 cursor-pointer select-none px-3 py-2 rounded-sm border border-zinc-300 bg-white hover:border-zinc-500 transition"
            data-testid="deviz-include-codes-toggle"
          >
            <input
              type="checkbox"
              checked={includeCodes}
              onChange={(e) => setIncludeCodes(e.target.checked)}
              className="h-4 w-4 accent-black"
              data-testid="deviz-include-codes-checkbox"
            />
            <span className="font-mono uppercase text-[11px] tracking-wider text-black font-semibold">
              Include coduri
            </span>
            <span className="font-mono text-[10px] text-zinc-500">
              (coloana Cod pe deviz)
            </span>
          </label>
        </div>
        {Object.keys(selections).length > 0 && (
          <Button
            variant="outline"
            onClick={resetSelections}
            className="rounded-sm border-zinc-300 h-10"
            data-testid="reset-deviz-button"
          >
            <RotateCcw size={14} className="mr-2" />
            Reseteaza
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2.2fr_1fr] gap-6 items-start">
        {/* Materials list grouped by category */}
        <div className="border border-zinc-200 rounded-sm bg-white">
          <div className="p-4 border-b border-zinc-200 bg-white space-y-2">
            <ColorPicker
              colors={allColors}
              selected={selectedColors}
              onChange={setSelectedColors}
              testIdPrefix="deviz-color"
            />
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cauta material..."
                className="rounded-sm border-zinc-300 focus-visible:ring-black pl-9 h-9"
                data-testid="deviz-materials-search-input"
              />
            </div>
          </div>
          <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-zinc-50">
                <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                    Material
                  </TableHead>
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-center w-14">
                    U.M.
                  </TableHead>
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide w-64 min-w-[13rem]">
                    Cantitate
                  </TableHead>
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide w-44">
                    Note
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grouped.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-zinc-500 py-8">
                      Niciun material gasit
                    </TableCell>
                  </TableRow>
                ) : (
                  grouped.map((group) => (
                    <React.Fragment key={group.id}>
                      <TableRow className="bg-zinc-100/70 hover:bg-zinc-100/70">
                        <TableCell colSpan={4} className="py-1.5">
                          <div className="flex items-center gap-2">
                            <Folder size={13} className="text-zinc-700" />
                            <span className="font-mono uppercase text-[11px] tracking-widest text-zinc-700 font-bold">
                              {group.name}
                            </span>
                            <span className="font-mono text-[10px] text-zinc-500">
                              ({group.items.length})
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                      {group.items.flatMap((m) =>
                        appColors(m).map((color) => renderMaterialRow(m, color))
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Right panel */}
        <div className="space-y-4">
          <div className="border border-zinc-200 rounded-sm bg-white p-5">
            <h2 className="font-display text-2xl font-bold tracking-tight text-black mb-4">
              Date client
            </h2>
            {/* Document number — auto by default; user can override. */}
            <div className="mb-4 pb-4 border-b border-dashed border-zinc-200">
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Numar document
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">
                  Seria COS ·
                </span>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={customNumber}
                  onChange={(e) => setCustomNumber(e.target.value)}
                  placeholder={
                    suggestedNumber != null
                      ? `auto: ${String(suggestedNumber).padStart(3, "0")}`
                      : "auto"
                  }
                  className="rounded-sm border-zinc-300 focus-visible:ring-black h-9 w-32 font-mono"
                  data-testid="deviz-number-input"
                />
                {customNumber && (
                  <button
                    type="button"
                    onClick={() => setCustomNumber("")}
                    className="font-mono uppercase text-[10px] tracking-wider text-zinc-500 hover:text-black"
                    data-testid="deviz-number-clear"
                    title="Foloseste numar auto"
                  >
                    auto
                  </button>
                )}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                Las gol pentru numar auto. Daca alegi unul mai mare, sequentia se sincronizeaza.
              </div>
            </div>
            <div className="mb-4 pb-4 border-b border-dashed border-zinc-200">
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide flex items-center gap-1.5">
                <UserCog size={12} /> Facturat de (agent)
              </Label>
              <div className="flex gap-1 mt-1">
                <Input
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="Numele tau"
                  className="rounded-sm border-zinc-300 focus-visible:ring-black flex-1"
                  data-testid="deviz-agent-name-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveAgentName}
                  disabled={savingAgent || agentName === (user?.agent_name || "")}
                  className="rounded-sm border-zinc-300 h-10 px-3"
                  title="Salveaza pentru a fi pre-completat la urmatoarele devizuri"
                  data-testid="deviz-agent-name-save"
                >
                  <Save size={12} />
                </Button>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                Apare pe deviz: "Facturat de: {agentName || "—"}"
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Nume
                </Label>
                <ClientAutocomplete
                  value={clientLastName}
                  onChange={setClientLastName}
                  onPick={pickClient}
                  placeholder="Popescu"
                  testid="deviz-client-lname"
                />
              </div>
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Prenume
                </Label>
                <Input
                  value={clientFirstName}
                  onChange={(e) => setClientFirstName(e.target.value)}
                  placeholder="Ion"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="deviz-client-fname"
                />
              </div>
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  C.I.F.
                </Label>
                <Input
                  value={clientCui}
                  onChange={(e) => setClientCui(e.target.value)}
                  placeholder="RO12345678"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="deviz-client-cui"
                />
              </div>
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  CNP
                </Label>
                <Input
                  value={clientCnp}
                  onChange={(e) => setClientCnp(e.target.value)}
                  placeholder="1980712123456"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="deviz-client-cnp"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Adresa
                </Label>
                <Input
                  value={clientAddress}
                  onChange={(e) => setClientAddress(e.target.value)}
                  placeholder="Str. Exemplu nr. 12, Bucuresti"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="deviz-client-address"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Telefon
                </Label>
                <Input
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="07xx xxx xxx"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="deviz-client-phone"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Nr. Reg. Com.
                </Label>
                <Input
                  value={clientRegCom}
                  onChange={(e) => setClientRegCom(e.target.value)}
                  placeholder="J33/xxxx/202x"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="deviz-client-reg-com"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Numele delegatului
                </Label>
                <Input
                  value={delegateName}
                  onChange={(e) => setDelegateName(e.target.value)}
                  placeholder="Popescu Ion"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="deviz-delegate-name"
                />
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 rounded-sm bg-white p-5">
            <h2 className="font-display text-2xl font-bold tracking-tight text-black mb-3">
              Deviz
            </h2>
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="max-h-[300px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-zinc-50">
                    <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                      <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                        Material
                      </TableHead>
                      <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-center w-12">
                        U.M.
                      </TableHead>
                      <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right w-16">
                        Cant.
                      </TableHead>
                      {includePrices && (
                        <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right w-20">
                          Total
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={includePrices ? 4 : 3} className="text-center text-zinc-500 py-6 text-sm">
                          Selecteaza materiale din stanga (cantitate &gt; 0)
                        </TableCell>
                      </TableRow>
                    ) : (
                      previewRows.map((r) => (
                        <TableRow key={`${r.material_id}::${r.color || "Mat"}`}>
                          <TableCell className="text-black text-sm">
                            {r.name}
                            {r.color ? (
                              <span className="ml-1.5 inline-block px-1.5 py-0 rounded-sm bg-zinc-100 border border-zinc-200 font-mono text-[10px] text-zinc-700 align-middle">
                                {r.color}
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-center text-zinc-700 font-mono text-xs">
                            {r.unit || "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold text-black">
                            {r.quantity}
                          </TableCell>
                          {includePrices && (
                            <TableCell className="text-right font-mono text-sm font-semibold text-black">
                              {formatRON(r.line_total || 0)}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {includePrices && previewRows.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-200 flex justify-between items-center">
                <span className="font-mono uppercase text-xs tracking-widest text-zinc-600">
                  Total de plata
                </span>
                <span
                  className="font-display text-2xl font-black tracking-tighter text-black"
                  data-testid="deviz-preview-total"
                >
                  {formatRON(previewTotal)}
                </span>
              </div>
            )}

            <Button
              onClick={downloadDocx}
              disabled={downloading || previewRows.length === 0}
              className="w-full mt-5 rounded-sm bg-black hover:bg-zinc-800 text-white h-11"
              data-testid="download-deviz-button"
            >
              <FileDown size={18} className="mr-2" />
              {downloading ? "Se genereaza..." : "Descarca deviz (DOCX)"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
